import { defineMessages, useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Select, SelectOption } from '@lifeomic/chroma-react/components/Select';
import { Test } from './types';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { UseHydrateOmicsExplorer } from './useHydrateOmicsExplorer';
import * as React from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';

const messages = defineMessages({
  selectATestLabel: {
    id: 'omicsExplorer.testSelector.selectATestLabel',
    defaultMessage: 'Select a test…',
  },
  fetchingTests: {
    id: 'omicsExplorer.testSelector.fetchingTests',
    defaultMessage: 'Fetching tests…',
  },
  noTestsAvailable: {
    id: 'omicsExplorer.testSelector.noTestsAvailable',
    defaultMessage: 'No tests available',
  },
  testTypeLabel: {
    id: 'omicsExplorer.testSelector.testTypeLabel',
    defaultMessage: 'Type:',
  },
  testDateLabel: {
    id: 'omicsExplorer.testSelector.testDateLabel',
    defaultMessage: 'Date:',
  },
  naLabel: {
    id: 'omicsExplorer.testSelector.naLabel',
    defaultMessage: 'N/A',
  },
  selectATest: {
    id: 'omicsExplorer.testSelector.selectATest',
    defaultMessage: 'Select a test',
  },
  testCaseLabel: {
    id: 'omicsExplorer.testSelector.testCaseLabel',
    defaultMessage: 'Case:',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    // Only temporary...
    // https://lifeomic.atlassian.net/browse/CHROM-163
    '& > button': {
      minWidth: theme.pxToRem(260),
      [theme.breakpoints.down('lg')]: {
        float: 'right',
      },
    },
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.6)',
  },
  selectedOptionValue: {
    color: theme.palette.text.secondary,
    fontSize: theme.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
    marginRight: theme.spacing(0.5),
  },
  selectedOptionLabel: {
    fontSize: theme.pxToRem(14),
    marginRight: theme.spacing(1.5),
    maxWidth: theme.pxToRem(128),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&:last-of-type': {
      marginRight: theme.spacing(1),
    },
    '&:empty': {
      '&:after': {
        content: "'—'",
      },
    },
  },
  labelForTestName: {
    fontWeight: theme.typography.fontWeightBold,
    marginRight: theme.spacing(3),
  },
  selectOption: {
    '& > div': {
      flexDirection: 'column',
    },
  },
  dataList: {
    margin: 0,
    width: '100%',
  },
  testOptionLabel: {
    color: theme.palette.black[500],
    fontSize: theme.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold,
    marginRight: theme.spacing(1),
    width: theme.pxToRem(48),
  },
  testOptionValue: {
    color: theme.palette.black[500],
    fontSize: theme.pxToRem(12),
    margin: 0,
    textAlign: 'left',
    width: '100%',
    '&:empty': {
      '&:after': {
        content: "'—'",
      },
    },
  },
  checked: {
    position: 'relative',
    '&::before': {
      background: theme.palette.primary.main,
      content: `''`,
      display: 'block',
      height: `calc(100% + ${theme.spacing(1)})`,
      position: 'absolute',
      left: theme.spacing(-2),
      top: theme.spacing(-0.5),
      width: 4,
    },
    '& *': {
      color: theme.palette.primary.main,
      '&$title': {
        fontWeight: 900,
      },
    },
  },
}));

interface TestSelectorProps {
  onTestSelection?: (test: Test) => void;
  selectedTest?: Test;
  state?: UseHydrateOmicsExplorer['state'];
  tests?: Array<Test>;
}

export const testIds = {
  testSelector: 'testSelector',
};

export const TestSelector: React.FC<TestSelectorProps> = ({
  onTestSelection,
  selectedTest,
  state,
  tests,
  ...rootProps
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const selectLabel =
    state === 'INITIALIZING'
      ? formatMessage(messages.fetchingTests)
      : state === 'COMPLETE' && tests?.length === 0
      ? formatMessage(messages.noTestsAvailable)
      : formatMessage(messages.selectATestLabel);

  return (
    <Select
      aria-label={selectLabel}
      className={classes.root}
      disabled={tests?.length === 0}
      label=""
      onChange={(_v: string, meta: Test) => onTestSelection?.(meta)}
      popoverAriaLabel={formatMessage(messages.selectATest)}
      value={selectedTest?.id}
      placeholder={selectLabel}
      selectedOptionDisplay={option => (
        <>
          <span
            className={clsx(
              classes.selectedOptionLabel,
              classes.labelForTestName,
            )}
          >
            {option?.meta?.name}
          </span>
          <span className={classes.selectedOptionLabel}>
            {option?.meta?.testType}
          </span>
          <span className={classes.selectedOptionLabel}>
            {(option?.meta?.indexedDate &&
              moment(option?.meta?.indexedDate).isValid() &&
              moment.utc(option?.meta?.indexedDate).format('YYYY-MM-DD')) ||
              formatMessage(messages.naLabel)}
          </span>
        </>
      )}
      {...getTestProps(testIds.testSelector)}
      {...rootProps}
    >
      {tests?.map(test => {
        return (
          <SelectOption
            className={classes.selectOption}
            key={test?.id}
            value={test?.id}
            meta={test}
            {...getTestProps(`${testIds.testSelector}-${test?.id}`)}
          >
            <Box
              direction="column"
              className={test?.id === selectedTest?.id ? classes.checked : ''}
            >
              <Text size="subbody" weight="bold">
                {test?.name}
              </Text>
              <dl className={classes.dataList}>
                <Box>
                  <dt className={classes.testOptionLabel}>
                    {formatMessage(messages.testTypeLabel)}
                  </dt>
                  <dd className={classes.testOptionValue}>{test?.testType}</dd>
                </Box>
                <Box>
                  <dt className={classes.testOptionLabel}>
                    {formatMessage(messages.testDateLabel)}
                  </dt>
                  <dd className={classes.testOptionValue}>
                    {(moment(test?.indexedDate).isValid() &&
                      moment.utc(test?.indexedDate).format('YYYY-MM-DD')) ||
                      formatMessage(messages.naLabel)}
                  </dd>
                </Box>
                {test?.caseId && (
                  <Box>
                    <dt className={classes.testOptionLabel}>
                      {formatMessage(messages.testCaseLabel)}
                    </dt>
                    <dd className={classes.testOptionValue}>{test?.caseId}</dd>
                  </Box>
                )}
              </dl>
            </Box>
          </SelectOption>
        );
      })}
    </Select>
  );
};
